import { t } from '@/i18n';
import { DateRangeSchema, isRS } from '@/utils';
import { z } from 'zod';

/// ////////////////////////////////////////////
/// Enum
/// ////////////////////////////////////////////

const PolicyLevelModel = z.union([z.literal(1), z.literal(2), z.literal(3)]);

export const PolicyLevelLabelModifier = {
  1: t('policy-level-1'),
  2: t('policy-level-2'),
  3: t('policy-level-3'),
} as const satisfies Record<z.infer<typeof PolicyLevelModel>, string>;

/// ////////////////////////////////////////////
/// Model
/// ////////////////////////////////////////////

export const PolicyBaseModel = z.object({
  id: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
  code: z.number(),
  name: z.string(),
  startYear: z.number().int().nullable(),
  endYear: z.number().int().nullable(),
  level: PolicyLevelModel,
  upperId: z.string().uuid().nullable(),
  organizationId: z.string().uuid().nullable(),
});

const PolicyBaseWithOrganizationNameModel = PolicyBaseModel.extend({
  organizationName: z.string().nullable(),
});

export const PoliciesWithOrganizationNameModel =
  PolicyBaseWithOrganizationNameModel.array();

type Policy = z.infer<typeof PolicyBaseModel> & {
  upper?: z.infer<typeof PolicyModel> | null;
};
type PolicyWithOrganizationName = z.infer<
  typeof PolicyBaseWithOrganizationNameModel
> & {
  upper?: z.infer<typeof PolicyModel> | null;
};

export const PolicyModel: z.ZodType<Policy> = PolicyBaseModel.extend({
  upper: z.lazy(() => PolicyModel.nullish()),
});
export const PolicyWithOrganizationNameModel: z.ZodType<PolicyWithOrganizationName> =
  PolicyBaseWithOrganizationNameModel.extend({
    upper: z.lazy(() => PolicyModel.nullish()),
  });

const PolicyOptionModel = PolicyBaseModel.pick({
  id: true,
  name: true,
  code: true,
  level: true,
});

type RecursiveOption = z.infer<typeof PolicyOptionModel> & {
  children: RecursiveOption[];
};
const NestedOptionElement: z.ZodType<RecursiveOption> =
  PolicyOptionModel.extend({
    children: z.lazy(() => NestedOptionElement.array()),
  });

export const NestedPolicyOptionModel = NestedOptionElement.array();

/// ////////////////////////////////////////////
/// Schema
/// ////////////////////////////////////////////

const PolicyFormBaseSchema = PolicyBaseModel.pick({
  name: true,
}).extend({
  name: PolicyBaseModel.shape.name.min(1),
  code: isRS() ? z.number().int().min(1) : z.string().min(1).max(6),
  level: z.number().int().min(1).max(PolicyLevelModel.options.length),
  period: DateRangeSchema.nullish(),
  upperIds: PolicyBaseModel.shape.upperId.unwrap().array().nullish(),
  organizationIds: PolicyBaseModel.shape.organizationId
    .unwrap()
    .array()
    .nullish(),
});

export const PolicyFormSchema = PolicyFormBaseSchema.refine(
  (data) => !(data.level === 1 && data.upperIds && data.upperIds.length > 0),
  {
    path: ['upperIds'],
    message: t('validation.non-selectable'),
  },
).refine((data) => !(data.level !== 1 && !data.upperIds?.length), {
  path: ['upperIds'],
  message: t('validation.no-upper-selected'),
});

export const UpdatePolicyParamsSchema = PolicyFormBaseSchema.extend({
  id: z.string().uuid(),
});
